/**
 * useSendMessage — sends a chat message that may carry a "moment" (a
 * timestamp, or a captured frame + timestamp) and the files staged with it.
 * Every file — the moment's frame included — uploads through the one
 * content-thread files endpoint (`uploadThreadFile`,
 * `FILE_UPLOAD_CONCURRENCY` at a time), then the message posts once with
 * `file_ids` and, for a frame, `anchor.frame.file_id`.
 */
import { useCallback, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';

import { ApiError } from '@discovr/common';
import type {
  AnchorFrame,
  AnchorShape,
  MediaMessageAnchor,
  MessageView,
  ThreadFileKind,
  ThreadFileView,
} from '@discovr/types';

import { uploadThreadFile, type UploadThreadFileArgs } from '../api/threadFiles';
import { createCampaignDetailApi } from '../api/campaignQueries';
import { campaignKeys, CONTENT_THREAD_TYPE } from '../queryKeys';
import { bumpRosterEntryToTop, TAB_FOR_THREAD } from './rosterCacheOps';

const api = createCampaignDetailApi();

const VERSION_STALE_CODE = 'VERSION_STALE';
/** The server refused an upload for its size. */
export const FILE_TOO_LARGE_CODE = 'FILE_TOO_LARGE';
/** The server refused an upload whose extension or bytes aren't an allowed type. */
export const FILE_TYPE_NOT_ALLOWED_CODE = 'FILE_TYPE_NOT_ALLOWED';
/** The server refused an image upload for its pixel dimensions. */
export const IMAGE_DIMENSIONS_NOT_ALLOWED_CODE = 'IMAGE_DIMENSIONS_NOT_ALLOWED';
/** The post's captured frame is over the server's frame limit. */
export const FRAME_TOO_LARGE_CODE = 'FRAME_TOO_LARGE';
/** Too many, or too large, uploads are waiting to be sent with a message. */
export const UPLOAD_QUOTA_EXCEEDED_CODE = 'UPLOAD_QUOTA_EXCEEDED';
/** Too many uploads are in flight at once. */
export const UPLOAD_CONCURRENCY_EXCEEDED_CODE = 'UPLOAD_CONCURRENCY_EXCEEDED';
/** Too many requests from this user: the per-user rate limiter's code (apperror.TooManyRequests). */
export const RATE_LIMITED_CODE = 'TOO_MANY_REQUESTS';
/** The post named uploaded files the server can no longer attach. */
const FILE_NOT_AVAILABLE_CODE = 'FILE_NOT_AVAILABLE';
/** Refused for good: resending the same file (or frame) can't succeed. */
const FILE_REJECTED_CODES: ReadonlySet<string> = new Set([
  FILE_TOO_LARGE_CODE,
  FILE_TYPE_NOT_ALLOWED_CODE,
  IMAGE_DIMENSIONS_NOT_ALLOWED_CODE,
  FRAME_TOO_LARGE_CODE,
]);
/**
 * Held back for now. The rate and concurrency limits clear after a wait; the upload quota only clears once
 * pending files are sent, deleted or swept (24 h), so waiting alone doesn't help it.
 */
const THROTTLED_CODES: ReadonlySet<string> = new Set([UPLOAD_QUOTA_EXCEEDED_CODE, UPLOAD_CONCURRENCY_EXCEEDED_CODE, RATE_LIMITED_CODE]);
const THREAD_LOCKED_STATUS = 423;
/** A gateway (ALB/WAF) answers these with an HTML page, so the code is no help: the status is all there is. */
const PAYLOAD_TOO_LARGE_STATUS = 413;
const TOO_MANY_REQUESTS_STATUS = 429;
/** Fallback message for a thrown value that isn't an `Error` (e.g. a raw
 *  string or object rejection) — `MessageSendError` always needs a message. */
const DEFAULT_SEND_ERROR_MESSAGE = 'send failed';

/**
 * The note POST has no WAF exemption, so its whole body must stay under the
 * 8 KB the WAF inspects; this is the stored markup's share of it.
 */
export const MAX_STORED_SHAPES_BYTES = 6 * 1024;
/** BE `limits.MaxFrameShapes`: an anchor carrying more is refused. */
export const MAX_FRAME_SHAPES = 50;

/** Uploads (the moment's frame, plus every file) run this many at a time. */
export const FILE_UPLOAD_CONCURRENCY = 2;

/** `SendCallbacks`' reserved index for the moment's frame — it isn't a member of `draft.files`. */
export const FRAME_ITEM_INDEX = -1;

/** A browser-compressed image (WebP, or JPEG where WebP can't be encoded). Kept out of `@discovr/types`, which stays free of browser globals. */
export interface EncodedImage {
  blob: Blob;
  contentType: 'image/webp' | 'image/jpeg';
  width: number;
  height: number;
}

/** What the server holds for an uploaded item: its file id and, for an image, the dimensions it read from the bytes. */
export interface UploadedFile {
  fileId: string;
  width?: number;
  height?: number;
}

/** A captured frame waiting to upload with its moment. */
export interface FrameDraft {
  image: EncodedImage;
  shapes: AnchorShape[];
  /** Upload name, e.g. "frame-0-08.webp". */
  name: string;
  /**
   * Set when an earlier send already uploaded this frame: it isn't uploaded
   * again, and the anchor keeps the dimensions that upload came back with.
   */
  uploaded?: UploadedFile;
}

/** One picked/captured file waiting to ride the message. */
export interface FileDraft {
  file: Blob;
  name: string;
  kind: ThreadFileKind;
  /** Set when an earlier send already uploaded this file: it isn't uploaded again. */
  fileId?: string;
}

/** What the composer hands to `useSendMessage` — optionally a moment
 *  (a timestamp, or a timestamp + captured frame), the files the server's
 *  policy let the composer stage, and a reply target. */
export interface MessageDraft {
  moment?: { tMs: number; frame?: FrameDraft };
  files: FileDraft[];
  replyTo?: string;
}

/**
 * What a failed send means for a retry: `version_stale`, `locked` and `file_rejected` never succeed as they are;
 * `file_unavailable` must upload again; `throttled` can succeed later, once the server's limit clears (a wait, or pending uploads sent or removed); `other` is worth retrying as it is.
 */
export type SendFailureKind = 'version_stale' | 'locked' | 'file_rejected' | 'file_unavailable' | 'throttled' | 'other';

export interface SendErrorDetails {
  /** The API error code, when the server answered with one. */
  code?: string;
  /** The name the failed item was uploading under; absent when the post itself failed. */
  fileName?: string;
}

/** Thrown by the sender on any failed send. `kind` tells the caller whether a
 *  retry can ever succeed; `code` and `fileName` say what the server refused
 *  and which file; `cause` keeps the original error for anyone further up
 *  the stack that needs more. */
export class MessageSendError extends Error {
  readonly kind: SendFailureKind;
  readonly code?: string;
  readonly fileName?: string;

  constructor(message: string, kind: SendFailureKind, cause?: unknown, { code, fileName }: SendErrorDetails = {}) {
    super(message, cause !== undefined ? { cause } : undefined);
    this.name = 'MessageSendError';
    this.kind = kind;
    this.code = code;
    this.fileName = fileName;
  }
}

/** An upload that failed, and the name its item was uploading under — so the send's error can say which file. */
class UploadFailure extends Error {
  readonly fileName: string;

  constructor(fileName: string, cause: unknown) {
    super(messageOf(cause), { cause });
    this.name = 'UploadFailure';
    this.fileName = fileName;
  }
}

export interface UseSendMessageArgs {
  entryId: string;
  campaignId?: string;
  versionId: string;
}

/** Upload progress for one item of the draft: an index into `draft.files`,
 *  or `FRAME_ITEM_INDEX` for the moment's frame. `fraction` is the 0..1
 *  share of that item's bytes sent so far. */
export interface SendProgress {
  (index: number, fraction: number): void;
}

/** What a send reports about each item of the draft as it goes, indexed as `SendProgress` is. */
export interface SendCallbacks {
  onProgress?: SendProgress;
  /** The item's upload came back — the server holds it as `uploaded`. Never fired on bytes sent
   *  alone (the server can still refuse a file after every byte went out), nor for an item the
   *  draft says an earlier send already uploaded. */
  onUploaded?: (index: number, uploaded: UploadedFile) => void;
}

export interface SendMessage {
  (body: string, draft: MessageDraft, callbacks?: SendCallbacks): Promise<MessageView>;
}

/** Maps a failed send onto its `SendFailureKind`. */
export function classifySendError(err: unknown): SendFailureKind {
  if (!(err instanceof ApiError)) return 'other';
  if (err.httpStatus === THREAD_LOCKED_STATUS) return 'locked';
  if (err.code === VERSION_STALE_CODE) return 'version_stale';
  if (FILE_REJECTED_CODES.has(err.code)) return 'file_rejected';
  if (err.code === FILE_NOT_AVAILABLE_CODE) return 'file_unavailable';
  if (THROTTLED_CODES.has(err.code)) return 'throttled';
  if (err.httpStatus === PAYLOAD_TOO_LARGE_STATUS) return 'file_rejected';
  if (err.httpStatus === TOO_MANY_REQUESTS_STATUS) return 'throttled';
  return 'other';
}

/** `MessageSendError` always needs a message, whatever was thrown. */
function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message;
  return DEFAULT_SEND_ERROR_MESSAGE;
}

/** What actually failed: an upload failure's own error, or the error itself. */
function underlyingError(err: unknown): unknown {
  if (err instanceof UploadFailure) return err.cause;
  return err;
}

function failedFileName(err: unknown): string | undefined {
  if (err instanceof UploadFailure) return err.fileName;
  return undefined;
}

function apiErrorCode(err: unknown): string | undefined {
  if (err instanceof ApiError) return err.code;
  return undefined;
}

/** A failed upload or post as the caller sees it: classified, with the server's code and — for an upload — which file. */
function toSendError(err: unknown): MessageSendError {
  const cause = underlyingError(err);
  return new MessageSendError(messageOf(cause), classifySendError(cause), cause, {
    code: apiErrorCode(cause),
    fileName: failedFileName(err),
  });
}

/** Uploads one item; a failure carries the item's name. */
async function uploadNamed(args: UploadThreadFileArgs): Promise<ThreadFileView> {
  try {
    return await uploadThreadFile(args);
  } catch (err) {
    throw new UploadFailure(args.name, err);
  }
}

/**
 * Client-only ids for send attempts. `crypto.randomUUID` throws on plain-http hosts and older WebViews, so a
 * timestamp + random suffix stands in; the ids never leave the browser.
 */
export function newLocalId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** State cached per draft object across retries: the idempotency key the
 *  first attempt minted, and — per upload already finished — its file id. */
interface DraftAttempt {
  clientId: string;
  /** The moment's frame, once the server holds it. */
  frame?: UploadedFile;
  /** Parallel to `draft.files` — filled in as each file's upload completes,
   *  so a retry only redoes what a previous attempt hasn't finished. */
  fileIds: (string | undefined)[];
  /** The send underway for this draft. Set synchronously, so a double click reuses it instead of uploading twice. */
  inFlight?: Promise<MessageView>;
}

function draftFileId(file: FileDraft): string | undefined {
  return file.fileId;
}

/** A fresh client id, starting from whatever an earlier send already uploaded so nothing uploads twice. */
function newAttempt(draft: MessageDraft): DraftAttempt {
  return { clientId: newLocalId(), frame: draft.moment?.frame?.uploaded, fileIds: draft.files.map(draftFileId) };
}

/** After the server declared the draft's uploads gone: a fresh client id and nothing uploaded, whatever ids the draft carries. */
function reuploadAttempt(draft: MessageDraft): DraftAttempt {
  return { clientId: newLocalId(), fileIds: new Array(draft.files.length) };
}

function toUploadedFile(view: ThreadFileView): UploadedFile {
  return { fileId: view.id, width: view.width, height: view.height };
}

/**
 * Runs `tasks` with at most `concurrency` in flight. The first failure stops new tasks, aborts the running ones
 * and is rethrown only once all have stopped, so a retry never races an upload still finishing.
 */
async function runWithConcurrency(tasks: Array<(signal: AbortSignal) => Promise<void>>, concurrency: number): Promise<void> {
  const controller = new AbortController();
  let next = 0;
  let failed = false;
  let firstError: unknown;

  async function worker(): Promise<void> {
    while (!failed && next < tasks.length) {
      const index = next;
      next += 1;
      const task = tasks[index];
      if (!task) continue;
      try {
        await task(controller.signal);
      } catch (err) {
        if (failed) continue;
        failed = true;
        firstError = err;
        controller.abort();
      }
    }
  }

  const workerCount = Math.min(concurrency, tasks.length);
  await Promise.all(Array.from({ length: workerCount }, worker));

  if (failed) throw firstError;
}

/** A `SendProgress` bound to one item's index — `undefined` when the caller
 *  passed no `onProgress` at all, so `uploadThreadFile` skips the wiring. */
function progressHandlerFor(index: number, onProgress: SendProgress | undefined): ((fraction: number) => void) | undefined {
  if (!onProgress) return undefined;
  const report = onProgress;
  function forward(fraction: number): void {
    report(index, fraction);
  }
  return forward;
}

/** Uploads the moment's frame, records what the server holds on the attempt, and says so. */
async function uploadFrame(
  entryId: string,
  frame: FrameDraft,
  attempt: DraftAttempt,
  callbacks: SendCallbacks | undefined,
  signal: AbortSignal,
): Promise<void> {
  const uploaded = await uploadNamed({
    entryId,
    file: frame.image.blob,
    name: frame.name,
    onProgress: progressHandlerFor(FRAME_ITEM_INDEX, callbacks?.onProgress),
    signal,
  });
  const held = toUploadedFile(uploaded);
  attempt.frame = held;
  callbacks?.onUploaded?.(FRAME_ITEM_INDEX, held);
}

/** Uploads one draft file, records its file id at its draft position, and says so. */
async function uploadFile(
  entryId: string,
  index: number,
  file: FileDraft,
  attempt: DraftAttempt,
  callbacks: SendCallbacks | undefined,
  signal: AbortSignal,
): Promise<void> {
  const uploaded = await uploadNamed({
    entryId,
    file: file.file,
    name: file.name,
    onProgress: progressHandlerFor(index, callbacks?.onProgress),
    signal,
  });
  attempt.fileIds[index] = uploaded.id;
  callbacks?.onUploaded?.(index, toUploadedFile(uploaded));
}

/** True once `uploadFile` has recorded a file id at this draft position. */
function isFileUploaded(attempt: DraftAttempt, index: number): boolean {
  return !!attempt.fileIds[index];
}

/** One upload task per draft file the attempt hasn't already finished. */
function pendingFileUploads(
  entryId: string,
  draft: MessageDraft,
  attempt: DraftAttempt,
  callbacks: SendCallbacks | undefined,
): Array<(signal: AbortSignal) => Promise<void>> {
  const tasks: Array<(signal: AbortSignal) => Promise<void>> = [];
  for (let index = 0; index < draft.files.length; index += 1) {
    if (isFileUploaded(attempt, index)) continue;
    const file = draft.files[index];
    if (!file) continue;
    tasks.push((signal) => uploadFile(entryId, index, file, attempt, callbacks, signal));
  }
  return tasks;
}

/** Every upload the attempt hasn't already completed — a retry only redoes these. */
function pendingUploads(
  entryId: string,
  draft: MessageDraft,
  attempt: DraftAttempt,
  callbacks: SendCallbacks | undefined,
): Array<(signal: AbortSignal) => Promise<void>> {
  const frame = draft.moment?.frame;
  const frameTask: Array<(signal: AbortSignal) => Promise<void>> =
    frame && !attempt.frame ? [(signal) => uploadFrame(entryId, frame, attempt, callbacks, signal)] : [];
  return [...frameTask, ...pendingFileUploads(entryId, draft, attempt, callbacks)];
}

interface FrameSize {
  width: number;
  height: number;
}

function hasServerSize(uploaded: UploadedFile): uploaded is UploadedFile & FrameSize {
  return uploaded.width !== undefined && uploaded.height !== undefined;
}

/** The frame size the server read from the image (it refuses an anchor that disagrees), else the local encode's. */
function anchorFrameSize(uploaded: UploadedFile, image: EncodedImage): FrameSize {
  if (hasServerSize(uploaded)) return { width: uploaded.width, height: uploaded.height };
  return { width: image.width, height: image.height };
}

function storedShapesBytes(shapes: AnchorShape[]): number {
  return new TextEncoder().encode(JSON.stringify(shapes)).length;
}

/**
 * The markup the anchor stores, or none when it would not fit: the marks are
 * baked into the frame image either way, so leaving the stored copy off keeps
 * the note sendable instead of failing every retry at the WAF or the server.
 */
function storableShapes(shapes: AnchorShape[]): AnchorShape[] | undefined {
  if (shapes.length > MAX_FRAME_SHAPES) return undefined;
  if (storedShapesBytes(shapes) > MAX_STORED_SHAPES_BYTES) return undefined;
  return shapes;
}

/** The anchor's frame sub-object — present only once the server holds the
 *  frame (never for a plain timestamp, and never mid-upload). */
function buildAnchorFrame(frame: FrameDraft | undefined, uploaded: UploadedFile | undefined): AnchorFrame | undefined {
  if (!frame || !uploaded) return undefined;
  return { file_id: uploaded.fileId, ...anchorFrameSize(uploaded, frame.image), shapes: storableShapes(frame.shapes) };
}

/** Builds the media anchor sent with the message — omitted when the draft carries no moment. */
function buildMomentAnchor(moment: MessageDraft['moment'], versionId: string, frame: UploadedFile | undefined): MediaMessageAnchor | undefined {
  if (!moment) return undefined;
  return { type: 'media', content_version_id: versionId, t_ms: Math.round(moment.tMs), frame: buildAnchorFrame(moment.frame, frame) };
}

/** Every upload has resolved by now, so a missing id is a bug, never a slot to drop. */
function resolvedFileId(id: string | undefined, index: number): string {
  if (id) return id;
  throw new Error(`useSendMessage: file ${index} finished uploading without recording a file id`);
}

/** The uploaded file ids, in draft order — omitted for a draft with no files. */
function resolvedFileIds(attempt: DraftAttempt): string[] | undefined {
  if (attempt.fileIds.length === 0) return undefined;
  return attempt.fileIds.map(resolvedFileId);
}

/** Refreshes the conversation, and floats the roster row: the sender gets no `new_message` SSE to do it. */
async function settleSent(queryClient: QueryClient, entryId: string, campaignId?: string): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: campaignKeys.conversation(entryId, CONTENT_THREAD_TYPE) });
  if (campaignId) {
    bumpRosterEntryToTop(queryClient, campaignId, entryId, TAB_FOR_THREAD.content);
    void queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(campaignId) });
  }
}

/** A failed settle only delays the refetch; the message is already posted, so it is logged, not thrown. */
function ignoreSettleFailure(entryId: string, err: unknown): void {
  console.warn(`[useSendMessage] cache settle failed for entry ${entryId} — the next refetch will pick up the message`, err);
}

/** In the background, so a failed settle can never report a posted message as failed (and invite a duplicate). */
function settleSentInBackground(queryClient: QueryClient, entryId: string, campaignId?: string): void {
  void settleSent(queryClient, entryId, campaignId).catch((err) => ignoreSettleFailure(entryId, err));
}

/** Uploads whatever the attempt hasn't already finished (up to
 *  `FILE_UPLOAD_CONCURRENCY` at a time), then posts. */
async function postMessage(
  entryId: string,
  versionId: string,
  draft: MessageDraft,
  attempt: DraftAttempt,
  body: string,
  callbacks: SendCallbacks | undefined,
): Promise<MessageView> {
  const pending = pendingUploads(entryId, draft, attempt, callbacks);
  if (pending.length > 0) {
    await runWithConcurrency(pending, FILE_UPLOAD_CONCURRENCY);
  }
  return api.postRosterNote(entryId, {
    body,
    client_id: attempt.clientId,
    reply_to: draft.replyTo,
    anchor: buildMomentAnchor(draft.moment, versionId, attempt.frame),
    file_ids: resolvedFileIds(attempt),
  });
}

/** One upload + post attempt: failures become `MessageSendError` (keeping
 *  the original error as `cause`), and the attempt stops being in flight
 *  either way. */
async function postAttempt(
  entryId: string,
  versionId: string,
  draft: MessageDraft,
  attempt: DraftAttempt,
  body: string,
  callbacks: SendCallbacks | undefined,
): Promise<MessageView> {
  try {
    return await postMessage(entryId, versionId, draft, attempt, body, callbacks);
  } catch (err) {
    throw toSendError(err);
  } finally {
    attempt.inFlight = undefined;
  }
}

/** The server can't attach what this attempt uploaded, so its file ids and client id are no use to a retry. */
function hasDeadUploads(err: unknown): boolean {
  return err instanceof MessageSendError && err.kind === 'file_unavailable';
}

/** The moment pointed at a cut that has since been replaced. */
function isVersionStale(err: unknown): boolean {
  return err instanceof MessageSendError && err.kind === 'version_stale';
}

/** After a `VERSION_STALE`, moves the player and later notes to the current cut instead of failing again. */
function refreshContentVersion(queryClient: QueryClient, entryId: string): void {
  void queryClient.invalidateQueries({ queryKey: campaignKeys.contentDetail(entryId) }).catch((err) => {
    console.warn(`[useSendMessage] content version refresh failed for entry ${entryId}`, err);
  });
}

/**
 * One upload + post. A failure keeps the attempt (and its uploads) for the retry, except dead uploads, which start
 * over under a new `client_id`.
 */
async function performSend(
  entryId: string,
  campaignId: string | undefined,
  versionId: string,
  queryClient: QueryClient,
  attempts: WeakMap<MessageDraft, DraftAttempt>,
  draft: MessageDraft,
  attempt: DraftAttempt,
  body: string,
  callbacks: SendCallbacks | undefined,
): Promise<MessageView> {
  try {
    const message = await postAttempt(entryId, versionId, draft, attempt, body, callbacks);
    attempts.delete(draft);
    settleSentInBackground(queryClient, entryId, campaignId);
    return message;
  } catch (err) {
    if (hasDeadUploads(err)) attempts.set(draft, reuploadAttempt(draft));
    if (isVersionStale(err)) refreshContentVersion(queryClient, entryId);
    throw err;
  }
}

/**
 * A retry-safe sender: uploads the draft's frame and files, then posts once. The `MessageDraft` object is the retry
 * key — the same object reuses its `client_id` and finished uploads; a new one is a new message.
 */
export function useSendMessage({ entryId, campaignId, versionId }: UseSendMessageArgs): SendMessage {
  const queryClient = useQueryClient();
  const attemptsRef = useRef(new WeakMap<MessageDraft, DraftAttempt>());

  const sendMessage = useCallback(
    (body: string, draft: MessageDraft, callbacks?: SendCallbacks): Promise<MessageView> => {
      const attempts = attemptsRef.current;
      const attempt = attempts.get(draft) ?? newAttempt(draft);
      attempts.set(draft, attempt);

      if (attempt.inFlight) {
        return attempt.inFlight;
      }

      const inFlight = performSend(entryId, campaignId, versionId, queryClient, attempts, draft, attempt, body, callbacks);
      attempt.inFlight = inFlight;
      return inFlight;
    },
    [entryId, campaignId, versionId, queryClient],
  );

  return sendMessage;
}
